import { produce } from 'immer';
import _ from 'lodash';

import {
  DefaultTemplateElementPosition,
  FormViewerNavigationType,
  NavigationStateType,
} from 'constants/forms';
import { checkIsNewBoolPdfDynamicField } from 'utils/forms';

import { FORM_ACTION_TYPE, FORM_PDF_ACTION_TYPE, FORMS_ACTION_TYPE } from 'store/forms/actions';

/**
 * @typedef FormTemplateHistoryEntity
 * @property {string} entryId History entity ID
 * @property {string} entityId Form ID
 * @property {string} userId
 * @property {string} typeOfChange
 * @property {string} userFullName
 * @property {string} changedAt
 */

/**
 * @typedef FormTemplateData
 * @property {string} template
 * @property {string} version
 * @property {string} createdByUserId
 * @property {string} modifiedByUserId
 * @property {string} createdDate
 * @property {string} lastModified
 * @property {string} formId
 * @property {string} companyId
 * @property {string} name
 * @property {string} state
 */

/**
 * @type {{
 * data: FormTemplateData | null,
 * history: Array<FormTemplateHistoryEntity> | null,
 * previewingTemplate: string | null,
 * isElementsChanged: boolean,
 * }}
 */
const initialState = {
  data: null,
  history: null,
  navigationStateType: NavigationStateType.VIEW,
  navigationType: FormViewerNavigationType.URL,
  previewingTemplate: null,
  isElementsChanged: false,
};

const formReducer = (state = initialState, { type, payload }) =>
  produce(state, draft => {
    switch (type) {
      case FORMS_ACTION_TYPE.UPDATE_FORM:
      case FORM_ACTION_TYPE.UPDATE_FORM_TEMPLATE:
      case FORM_ACTION_TYPE.GET_FORM_TEMPLATE:
      case FORM_PDF_ACTION_TYPE.UPDATE_FORM_TEMPLATE:

      case FORM_PDF_ACTION_TYPE.GET_FORM_TEMPLATE: {
        const sameFormData = state.data?.formId === payload.formId ? state.data : null;

        draft.data = {
          ...payload,
          pdfLink: payload.pdfLink || sameFormData?.pdfLink,
          pdfMetadata: payload.pdfMetadata || sameFormData?.pdfMetadata,
          template: _.has(payload, 'template') ? payload.template : sameFormData?.template,
          templateJson: _.has(payload, 'templateJson')
            ? payload.templateJson
            : (sameFormData?.templateJson ?? null),
          isTemplateSavedInPrism: _.has(payload, 'isTemplateSavedInPrism')
            ? payload.isTemplateSavedInPrism
            : !!sameFormData?.isTemplateSavedInPrism,
          initialPdfMetadata: payload.pdfMetadata
            ? _.cloneDeep(payload.pdfMetadata)
            : sameFormData?.pdfMetadata,
        };

        draft.isElementsChanged = false;
        draft.history = null;

        break;
      }

      case FORM_ACTION_TYPE.UPDATE_TEMPLATE_ELEMENT:

      case FORM_PDF_ACTION_TYPE.UPDATE_TEMPLATE_ELEMENT: {
        const element = draft.data.pdfMetadata.elements[payload.elementIndex];

        if (!element) {
          break;
        }

        element.position.x = payload.updatedProps.x || element.position.x;
        element.position.y = payload.updatedProps.y || element.position.y;
        element.position.height = parseInt(payload.updatedProps.height || element.position.height);
        element.position.width = parseInt(payload.updatedProps.width || element.position.width);

        if (payload.updatedField !== 'position') {
          element[payload.updatedField] = payload.updatedProps;

          if (checkIsNewBoolPdfDynamicField(element, draft.data.formType)) {
            element.position.width = element.position.height;
          }
        }

        element.position.isNewItem = false;
        draft.isElementsChanged = true;

        break;
      }

      case FORM_PDF_ACTION_TYPE.UPDATE_TEMPLATE_ELEMENT_MULTIPLE_OPTIONS: {
        const { dependenceGroups, elements } = payload;
        draft.data.pdfMetadata.dependenceGroups = dependenceGroups;
        draft.data.pdfMetadata.elements = elements;

        break;
      }

      case FORM_ACTION_TYPE.CREATE_TEMPLATE_ELEMENT:

      case FORM_PDF_ACTION_TYPE.CREATE_TEMPLATE_ELEMENT: {
        const { data } = draft;

        if (!data.pdfMetadata) {
          data.pdfMetadata = { elements: [] };
        }

        const isPrefilledElement = !_.isNil(payload.field);

        data.pdfMetadata.elements.push({
          position: {
            ...DefaultTemplateElementPosition[payload.fieldType],
            ...payload.position,
            page: payload.selectedPage,
            isNewItem: !isPrefilledElement,
          },
          [payload.fieldType]: isPrefilledElement ? payload.field : {},
        });

        draft.isElementsChanged = true;

        break;
      }

      case FORM_ACTION_TYPE.DELETE_TEMPLATE_ELEMENT:

      case FORM_PDF_ACTION_TYPE.DELETE_TEMPLATE_ELEMENT: {
        draft.data.pdfMetadata.elements.splice(payload.elementIndex, 1);
        draft.isElementsChanged = true;

        break;
      }

      case FORM_PDF_ACTION_TYPE.RESET_PDF_METADATA: {
        draft.isElementsChanged = false;
        draft.data.pdfMetadata = _.cloneDeep(draft.data?.initialPdfMetadata);

        break;
      }

      case FORM_ACTION_TYPE.GET_FORM_TEMPLATE_FROM_HISTORY:

      case FORM_PDF_ACTION_TYPE.GET_FORM_TEMPLATE_FROM_HISTORY: {
        draft.data = payload;
        break;
      }

      case FORM_ACTION_TYPE.GET_FORM_TEMPLATE_HISTORY: {
        draft.history = payload;
        break;
      }

      case FORM_ACTION_TYPE.RESET_FORM_TEMPLATE: {
        draft.data = null;
        break;
      }

      case FORM_PDF_ACTION_TYPE.SET_PREVIEWING_FORM_TEMPLATE: {
        draft.previewingTemplate = payload;
        break;
      }

      case FORM_ACTION_TYPE.SET_FORM_NAVIGATION_STATE_TYPE: {
        draft.navigationStateType = payload;
        break;
      }

      case FORM_ACTION_TYPE.SET_FORM_NAVIGATION_TYPE: {
        draft.navigationType = payload;
        break;
      }

      default:
        return state;
    }
  });

export default formReducer;
